import { useBrandsWheels, useVehiclesWheels } from '@workspace/core-ts/api';
import { Input, LoaderScreen, Modal } from '@workspace/core-ts/components';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import type { FormSchemaType } from '@/schema';

interface Brand {
  id: number;
  name: string;
  img: string;
}

interface Model {
  sra: string;
  modele: string;
  annee_debut_version: string;
  annee_fin_version: string;
  prix_origine: number;
}

// favorites brands for 50cc
const favorites = ['DERBI', 'KYMCO', 'MBK', 'PEUGEOT', 'PIAGGIO', 'YAMAHA'];

export default function ModalVehiculier() {
  const methods = useFormContext<FormSchemaType>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState<'brand' | 'model'>('brand');
  const index = searchParams.get('index') ? parseInt(searchParams.get('index')!) : 0;

  const [searchMarque, setSearchMarque] = useState('');
  const [brand, setBrand] = useState<Brand | null>(null);

  const [searchModel, setSearchModel] = useState('');
  const [model, setModel] = useState<Model | null>(null);

  const queryBrands = useBrandsWheels(50);
  const queryModels = useVehiclesWheels(50, brand?.id, 50);

  const filteredBrands = useMemo(() => {
    if (!queryBrands.data?.data) return [];

    let temp = [...queryBrands.data.data]
      .sort((a, b) => a.name.localeCompare(b.name))
      .sort((a, b) => favorites.includes(b.name).toString().localeCompare(favorites.includes(a.name).toString()));

    if (searchMarque) temp = temp.filter((brand) => brand.name.toLowerCase().includes(searchMarque.toLowerCase()) && brand.name && brand.name.length > 1);

    return temp;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryBrands.data?.data, searchMarque, favorites]);

  const filteredModels = useMemo(() => {
    if (!queryModels.data?.data) return [];

    let temp = [...queryModels.data.data].sort((a, b) => a.modele.localeCompare(b.modele));

    if (searchModel) temp = temp.filter((model) => model.modele.toLowerCase().includes(searchModel.toLowerCase()) && model.modele);

    return temp;
  }, [queryModels.data?.data, searchModel]);

  useEffect(() => {
    const brandForm = methods.watch(`DemandeTarif.ListeVehicules.${index}.Marque`);
    const modelForm = methods.watch(`DemandeTarif.ListeVehicules.${index}.Modele`);

    // if (queryBrands.isLoading || queryModels.isLoading || !modelForm || !brandForm) return;

    setStep('brand');
    setSearchMarque('');
    setSearchModel('');

    if (!brandForm) setBrand(null);
    else setBrand(filteredBrands?.find((brand: any) => brand.name === brandForm) || null);

    if (!modelForm) setModel(null);
    else setModel(filteredModels?.find((model: any) => model.modele === modelForm) || null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const handleSelectBrand = (brand: Brand) => {
    setBrand(brand);
    setModel(null);
    setStep('model');
  };

  const handleSelectModel = (model: Model) => {
    setModel(model);
    if (!brand) return;
    methods.setValue(`DemandeTarif.ListeVehicules.${index}.Marque`, brand.name);
    methods.setValue(`DemandeTarif.ListeVehicules.${index}.Modele`, model.modele);
    methods.setValue(`DemandeTarif.ListeVehicules.${index}.IdentifiantSRA`, model.sra);
    methods.setValue(`DemandeTarif.ListeVehicules.${index}.PrixOrigine`, model.prix_origine);

    setSearchParams();
  };

  const titles = {
    brand: 'Sélectionnez votre marque',
    model: 'Sélectionnez votre modèle',
  };

  return (
    <Modal
      className="max-w-3xl h-[calc(100vh-100px)]"
      onClose={() => setSearchParams()}
      open={searchParams.get('modal') === 'vehiculier'}
      title={titles[step]}
    >
      <TransitionModalVehiculier show={step === 'brand'}>
        <Modal.Body className="flex gap-2 pt-3 border-b border-border pb-4">
          {brand ? (
            <button
              className="border border-border rounded-md px-3 py-1 leading-none text-sm ml-auto hover:bg-gray-50 whitespace-nowrap"
              onClick={() => {
                setStep('brand');
              }}
              type="button"
            >
              {brand.name}
            </button>
          ) : null}
          {model ? (
            <button
              className="border border-border rounded-md px-3 py-1 leading-none text-sm ml-auto hover:bg-gray-50 whitespace-nowrap"
              onClick={() => {
                setStep('model');
              }}
              type="button"
            >
              {model.modele}
            </button>
          ) : null}
          <Input
            className="text-sm"
            onChange={(e: any) => {
              setSearchMarque(e.target.value);
            }}
            placeholder="Recherchez votre marque"
            value={searchMarque}
          />
        </Modal.Body>

        <Modal.Body className="flex flex-col overflow-y-scroll">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 my-4">
            {filteredBrands?.map((brand: any) => (
              <button
                className="flex flex-col items-center justify-center p-2 text-center space-y-1.5 h-36 border border-border rounded-md hover:bg-gray-50 cursor-pointer"
                data-brand={brand.name}
                key={`brand-${brand.id}`}
                onClick={() => {
                  handleSelectBrand(brand);
                }}
                type="button"
              >
                <div className="h-16 w-16 sm:w-20 sm:h-20 flex items-center justify-center">
                  {brand.img ? (
                    <img
                      alt={brand.name}
                      className="pointer-events-none select-none"
                      src={brand.img}
                    />
                  ) : null}
                </div>
                <span>{brand.name}</span>
              </button>
            ))}
          </div>
        </Modal.Body>
      </TransitionModalVehiculier>

      <TransitionModalVehiculier show={step === 'model'}>
        <Modal.Body className="flex gap-2 pt-3 border-b border-border pb-4">
          {brand ? (
            <button
              className="border border-border rounded-md px-3 py-1 leading-none text-sm ml-auto hover:bg-gray-50 whitespace-nowrap"
              onClick={() => {
                setStep('brand');
              }}
              type="button"
            >
              {brand.name}
            </button>
          ) : null}
          {model ? (
            <button
              className="border border-border rounded-md px-3 py-1 leading-none text-sm ml-auto hover:bg-gray-50 whitespace-nowrap"
              onClick={() => {
                setStep('model');
              }}
              type="button"
            >
              {model.modele}
            </button>
          ) : null}
          <Input
            className="text-sm"
            onChange={(e: any) => {
              setSearchModel(e.target.value);
            }}
            placeholder="Recherchez votre modèle"
            value={searchModel}
          />
        </Modal.Body>
        <Modal.Body className="flex flex-col overflow-y-scroll">
          {queryModels.isLoading ? (
            <LoaderScreen />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 my-4">
              {filteredModels?.map((model: any) => (
                <button
                  className="flex flex-col items-center justify-center space-y-1.5 p-2 text-center h-24 border border-border rounded-md hover:bg-gray-50 cursor-pointer"
                  data-model={model.modele}
                  key={`model-${model.sra}`}
                  onClick={() => {
                    handleSelectModel(model);
                  }}
                  type="button"
                >
                  <p>{model.modele}</p>

                  <p className="text-xs text-gray-500">
                    {model.annee_debut_version ? `depuis ${model.annee_debut_version}` : ''}{' '}
                    {model.annee_fin_version ? `jusqu'à ${model.annee_fin_version}` : ''}
                  </p>
                </button>
              ))}
            </div>
          )}
        </Modal.Body>
      </TransitionModalVehiculier>
    </Modal>
  );
}

interface TransitionModalVehiculierProps {
  show?: boolean;
  children?: React.ReactNode;
}

function TransitionModalVehiculier({ show = false, children }: TransitionModalVehiculierProps) {
  if (!show) {
    return null;
  }

  return <>{children}</>;
}
